import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useManagedBasePolicyOptions } from 'src/hooks/environmentWdacPolicy';
import { getNextPolicyVersion } from 'src/utils/policyVersion';
import BasePolicySelectionForm from './BasePolicySelectionForm';
import BasePolicySubView, {
  ADD_BASE_POLICY_VIEW,
  BASE_POLICY_SUB_VIEW_TITLES,
  USE_OTHER_BASE_POLICY_VIEW,
} from './BasePolicySubView';

const SELECTION_VIEW = 'selection';

// Generating a security catalog and generating a packaged apps policy are the same flow over the
// same set of managed base policies, so both run through here and supply only their own copy.
const GenerateSupplementalModal = ({
  createSubmitLabel,
  createTitle,
  description,
  icon,
  itemCount,
  loading,
  mode,
  onClose,
  onSave,
  open,
  policyState,
  requirementNote,
  returnTarget,
  summary,
  updateSubmitLabel,
  updateTitle,
  warningNote,
}) => {
  const isUpdate = mode === 'update';
  const [view, setView] = useState(SELECTION_VIEW);
  const [selectedBasePolicyIds, setSelectedBasePolicyIds] = useState([]);
  const initializedRef = useRef(false);

  const { data: optionsData, isLoading: basePoliciesLoading } = useManagedBasePolicyOptions({
    enabled: open,
  });

  const basePolicies = useMemo(() => optionsData?.policies || [], [optionsData]);
  const activePolicies = useMemo(
    () => (policyState?.policies || []).filter(policy => policy.isActive),
    [policyState],
  );

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setView(SELECTION_VIEW);
      return;
    }
    if (initializedRef.current || basePoliciesLoading) {
      return;
    }

    // An update starts from what is already deployed, narrowed to bases that are still selectable;
    // a first generation starts from the environment defaults.
    const selectableIds = new Set(basePolicies.map(policy => policy.policyId));
    setSelectedBasePolicyIds(
      isUpdate
        ? activePolicies
            .map(policy => policy.basePolicyId)
            .filter(policyId => selectableIds.has(policyId))
        : basePolicies.filter(policy => policy.environmentDefault).map(policy => policy.policyId),
    );
    initializedRef.current = true;
  }, [open, isUpdate, basePoliciesLoading, activePolicies, basePolicies]);

  const showSelection = () => setView(SELECTION_VIEW);

  const handleDialogClose = () => {
    if (view === SELECTION_VIEW) {
      onClose();
      return;
    }
    showSelection();
  };

  const openSubView = nextView => currentBasePolicyIds => {
    setSelectedBasePolicyIds(currentBasePolicyIds);
    setView(nextView);
  };

  // Whichever way a base policy came under management, it is the one just asked for, so it comes
  // back selected.
  const selectAndReturn = policy => {
    setSelectedBasePolicyIds(current =>
      current.includes(policy.policyId) ? current : [...current, policy.policyId],
    );
    showSelection();
  };

  const subView = BASE_POLICY_SUB_VIEW_TITLES[view];

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon={subView?.icon || icon} width={22} height={22} color="#87C8E0" />
        {subView?.title || (isUpdate ? updateTitle : createTitle)}
      </DialogTitle>

      {subView ? (
        <BasePolicySubView
          view={view}
          returnTarget={returnTarget}
          onDone={selectAndReturn}
          onCancel={showSelection}
        />
      ) : (
        <BasePolicySelectionForm
          basePolicies={basePolicies}
          basePoliciesLoading={basePoliciesLoading}
          description={description}
          initialBasePolicyIds={selectedBasePolicyIds}
          loading={loading}
          nextVersion={isUpdate ? getNextPolicyVersion(policyState?.currentVersion) : '1.0.0.0'}
          onAddBasePolicy={openSubView(ADD_BASE_POLICY_VIEW)}
          onCancel={onClose}
          onSubmit={onSave}
          onUseOtherBasePolicy={openSubView(USE_OTHER_BASE_POLICY_VIEW)}
          requirementNote={requirementNote}
          submitDisabled={itemCount === 0}
          submitLabel={isUpdate ? updateSubmitLabel : createSubmitLabel}
          summary={summary}
          warningNote={warningNote}
        />
      )}
    </Dialog>
  );
};

GenerateSupplementalModal.propTypes = {
  createSubmitLabel: PropTypes.string.isRequired,
  createTitle: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  itemCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'update']).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool,
  policyState: PropTypes.shape({
    currentVersion: PropTypes.string,
    policies: PropTypes.arrayOf(
      PropTypes.shape({
        basePolicyId: PropTypes.string,
        isActive: PropTypes.bool,
      }),
    ),
  }),
  requirementNote: PropTypes.string.isRequired,
  returnTarget: PropTypes.string.isRequired,
  summary: PropTypes.node,
  updateSubmitLabel: PropTypes.string.isRequired,
  updateTitle: PropTypes.string.isRequired,
  warningNote: PropTypes.node,
};

export default GenerateSupplementalModal;
